import { Injectable } from '@angular/core';
import type {
  IOvieHealthAgentNumberRequest,
  IOvieHealthAgentNumberResponse,
  IOvieHealthAnalyticsRequest,
  IOvieHealthAnalyticsResponse,
  IOvieHealthCallsRequest,
  IOvieHealthCallsResponse,
  IOvieHealthError,
  IOvieHealthKnowledgeBaseRequest,
  IOvieHealthKnowledgeBaseResponse,
  IOvieHealthSettingsRequest,
  IOvieHealthSettingsResponse,
  OvieHealthErrorCode,
} from '@principle-theorem/principle-core/interfaces';
import {
  OvieHealthAnalyticsAction,
  OvieHealthCallsAction,
  OvieHealthKnowledgeBaseAction,
  OvieHealthSettingsAction,
} from '@principle-theorem/principle-core/interfaces';
import {
  httpsCallable,
  isObject,
  serialise,
  snapshot,
  unserialise,
  type SerialisedData,
} from '@principle-theorem/shared';

@Injectable({ providedIn: 'root' })
export class OvieHealthCallableService {
  async analytics<A extends OvieHealthAnalyticsAction>(
    request: IOvieHealthAnalyticsRequest<A>
  ): Promise<IOvieHealthAnalyticsResponse<A>> {
    return this._callOvie<
      IOvieHealthAnalyticsRequest<A>,
      IOvieHealthAnalyticsResponse<A>
    >('http-ovie-analytics', request);
  }

  async calls<A extends OvieHealthCallsAction>(
    request: IOvieHealthCallsRequest<A>
  ): Promise<IOvieHealthCallsResponse<A>> {
    return this._callOvie<
      IOvieHealthCallsRequest<A>,
      IOvieHealthCallsResponse<A>
    >('http-ovie-calls', request);
  }

  async knowledgeBase<A extends OvieHealthKnowledgeBaseAction>(
    request: IOvieHealthKnowledgeBaseRequest<A>
  ): Promise<IOvieHealthKnowledgeBaseResponse<A>> {
    return this._callOvie<
      IOvieHealthKnowledgeBaseRequest<A>,
      IOvieHealthKnowledgeBaseResponse<A>
    >('http-ovie-knowledgeBase', request);
  }

  async settings<A extends OvieHealthSettingsAction>(
    request: IOvieHealthSettingsRequest<A>
  ): Promise<IOvieHealthSettingsResponse<A>> {
    return this._callOvie<
      IOvieHealthSettingsRequest<A>,
      IOvieHealthSettingsResponse<A>
    >('http-ovie-settings', request);
  }

  async agentNumber(
    request: IOvieHealthAgentNumberRequest
  ): Promise<IOvieHealthAgentNumberResponse> {
    return this._callOvie<
      IOvieHealthAgentNumberRequest,
      IOvieHealthAgentNumberResponse
    >('http-ovie-agentNumber', request);
  }

  private async _callOvie<Req, Res>(
    functionName: string,
    request: Req
  ): Promise<Res> {
    try {
      const callable = httpsCallable<SerialisedData<Req>, SerialisedData<Res>>(
        functionName
      );
      const serialised = await snapshot(callable(serialise(request)));
      return unserialise(serialised) as Res;
    } catch (err) {
      const ovieError = extractOvieError(err);
      if (ovieError) {
        throw new OvieCallableError(ovieError);
      }
      throw err;
    }
  }
}

export class OvieCallableError extends Error {
  readonly code: OvieHealthErrorCode;
  readonly upstreamStatus?: number;
  readonly retryAfterMs?: number;

  constructor(ovieError: IOvieHealthError) {
    super(ovieError.message);
    this.name = 'OvieCallableError';
    this.code = ovieError.code;
    this.upstreamStatus = ovieError.upstreamStatus;
    this.retryAfterMs = ovieError.retryAfterMs;
  }
}

function extractOvieError(err: unknown): IOvieHealthError | undefined {
  if (!isObject(err) || !('details' in err)) {
    return undefined;
  }
  const details = err['details'];
  if (!isObject(details) || !('ovieError' in details)) {
    return undefined;
  }
  const ovieError = details['ovieError'];
  if (isObject(ovieError) && 'code' in ovieError && 'message' in ovieError) {
    return {
      ...ovieError,
      code: ovieError.code as OvieHealthErrorCode,
    } as IOvieHealthError;
  }
  return undefined;
}
